import {
  Color,
  type ColorRepresentation,
  FrontSide,
  LessEqualDepth,
  type Material,
  Mesh,
  SkinnedMesh,
  type Object3D,
} from 'three';

import { applyKeyPoseSnapshotToScene } from '../keyPoseSnapshots';
import type { KeyPoseSkeletonSnapshot } from '../types';

interface ApplyGhostSceneMaterialsOptions {
  color: ColorRepresentation;
  opacity: number;
  renderOrder: number;
}

interface ApplyTexturedGhostSceneMaterialsOptions
  extends ApplyGhostSceneMaterialsOptions {
  tintAmount: number;
}

interface ApplyGhostDepthPrepassMaterialsOptions {
  renderOrder: number;
}

type GhostMaterial = Material & {
  color?: Color;
  emissive?: Color;
  emissiveMap?: unknown;
  map?: unknown;
  metalness?: number;
  roughness?: number;
  side?: number;
};

const ignoreRaycast = () => {};

export function applyGhostSceneMaterials(
  ghostScene: Object3D,
  { color, opacity, renderOrder }: ApplyGhostSceneMaterialsOptions,
) {
  const materials: Material[] = [];
  const ghostColor = new Color(color);

  ghostScene.traverse((object) => {
    if (!(object instanceof Mesh)) {
      return;
    }

    object.castShadow = false;
    object.receiveShadow = false;
    object.raycast = ignoreRaycast;
    object.renderOrder = renderOrder;

    const sourceMaterial = object.material;
    const sourceMaterials = Array.isArray(sourceMaterial)
      ? sourceMaterial
      : [sourceMaterial];
    const ghostMaterials = sourceMaterials.map((material) => {
      const ghostMaterial = material.clone() as GhostMaterial;

      ghostMaterial.side = FrontSide;
      ghostMaterial.transparent = true;
      ghostMaterial.opacity = opacity;
      ghostMaterial.depthTest = true;
      ghostMaterial.depthWrite = false;

      if (ghostMaterial.color instanceof Color) {
        ghostMaterial.color.copy(ghostColor);
      }

      if (ghostMaterial.emissive instanceof Color) {
        ghostMaterial.emissive.setScalar(0);
      }

      if ('map' in ghostMaterial) {
        ghostMaterial.map = null;
      }

      if ('emissiveMap' in ghostMaterial) {
        ghostMaterial.emissiveMap = null;
      }

      if (typeof ghostMaterial.metalness === 'number') {
        ghostMaterial.metalness = 0.08;
      }

      if (typeof ghostMaterial.roughness === 'number') {
        ghostMaterial.roughness = 0.92;
      }

      ghostMaterial.needsUpdate = true;
      materials.push(ghostMaterial);

      return ghostMaterial;
    });

    object.material = Array.isArray(sourceMaterial)
      ? ghostMaterials
      : ghostMaterials[0];
  });

  return materials;
}

export function applyTexturedGhostSceneMaterials(
  ghostScene: Object3D,
  {
    color,
    opacity,
    renderOrder,
    tintAmount,
  }: ApplyTexturedGhostSceneMaterialsOptions,
) {
  const materials: Material[] = [];
  const ghostColor = new Color(color);
  const resolvedTintAmount = clamp(tintAmount, 0, 1);

  ghostScene.traverse((object) => {
    if (!(object instanceof Mesh)) {
      return;
    }

    object.castShadow = false;
    object.receiveShadow = false;
    object.raycast = ignoreRaycast;
    object.renderOrder = renderOrder;

    const sourceMaterial = object.material;
    const sourceMaterials = Array.isArray(sourceMaterial)
      ? sourceMaterial
      : [sourceMaterial];
    const ghostMaterials = sourceMaterials.map((material) => {
      const ghostMaterial = material.clone() as GhostMaterial;

      ghostMaterial.side = FrontSide;
      ghostMaterial.transparent = true;
      ghostMaterial.opacity = opacity;
      ghostMaterial.depthTest = true;
      ghostMaterial.depthFunc = LessEqualDepth;
      ghostMaterial.depthWrite = false;

      if (ghostMaterial.color instanceof Color && resolvedTintAmount > 0) {
        ghostMaterial.color.lerp(ghostColor, resolvedTintAmount);
      }

      ghostMaterial.needsUpdate = true;
      materials.push(ghostMaterial);

      return ghostMaterial;
    });

    object.material = Array.isArray(sourceMaterial)
      ? ghostMaterials
      : ghostMaterials[0];
  });

  return materials;
}

export function applyGhostDepthPrepassMaterials(
  ghostScene: Object3D,
  { renderOrder }: ApplyGhostDepthPrepassMaterialsOptions,
) {
  const materials: Material[] = [];

  ghostScene.traverse((object) => {
    if (!(object instanceof Mesh)) {
      return;
    }

    object.castShadow = false;
    object.receiveShadow = false;
    object.raycast = ignoreRaycast;
    object.renderOrder = renderOrder;

    const sourceMaterial = object.material;
    const sourceMaterials = Array.isArray(sourceMaterial)
      ? sourceMaterial
      : [sourceMaterial];
    const depthMaterials = sourceMaterials.map((material) => {
      const depthMaterial = material.clone() as GhostMaterial;

      depthMaterial.side = FrontSide;
      depthMaterial.transparent = false;
      depthMaterial.opacity = 1;
      depthMaterial.depthTest = true;
      depthMaterial.depthFunc = LessEqualDepth;
      depthMaterial.depthWrite = true;
      depthMaterial.colorWrite = false;
      depthMaterial.needsUpdate = true;
      materials.push(depthMaterial);

      return depthMaterial;
    });

    object.material = Array.isArray(sourceMaterial)
      ? depthMaterials
      : depthMaterials[0];
  });

  return materials;
}

export function applyEditableGhostSceneMaterials(ghostScene: Object3D) {
  const materials: Material[] = [];

  ghostScene.traverse((object) => {
    object.raycast = ignoreRaycast;

    if (!(object instanceof Mesh)) {
      return;
    }

    const sourceMaterial = object.material;
    const sourceMaterials = Array.isArray(sourceMaterial)
      ? sourceMaterial
      : [sourceMaterial];
    const editableMaterials = sourceMaterials.map((material) => {
      const editableMaterial = material.clone();

      editableMaterial.needsUpdate = true;
      materials.push(editableMaterial);

      return editableMaterial;
    });

    object.material = Array.isArray(sourceMaterial)
      ? editableMaterials
      : editableMaterials[0];
  });

  return materials;
}

export function freezeGhostScene(ghostScene: Object3D) {
  ghostScene.updateMatrixWorld(true);

  ghostScene.traverse((object) => {
    object.updateMatrix();
    object.updateMatrixWorld(true);
    object.matrixAutoUpdate = false;
    object.matrixWorldAutoUpdate = false;
  });
}

/**
 * Applies a pose to a frozen ghost. Frozen nodes keep `matrixWorldAutoUpdate`
 * false, so we must temporarily re-enable world-matrix updates for the renderer.
 */
export function applyKeyPoseSnapshotToFrozenGhostScene(
  ghostScene: Object3D,
  skeletonSnapshot: KeyPoseSkeletonSnapshot,
) {
  applyKeyPoseSnapshotToScene(ghostScene, skeletonSnapshot);

  ghostScene.traverse((object) => {
    object.matrixWorldAutoUpdate = true;
  });
  ghostScene.updateMatrixWorld(true);

  ghostScene.traverse((object) => {
    object.matrixWorldAutoUpdate = false;
    object.matrixWorldNeedsUpdate = false;

    if (object instanceof SkinnedMesh && object.skeleton) {
      object.skeleton.update();
    }
  });
}

function clamp(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) {
    return min;
  }

  return Math.min(max, Math.max(min, value));
}
